/**
 * HTML/CSS card preview renderer.
 *
 * Renders a CardInput as a scaled HTML element that visually approximates
 * the ODP slide. Uses CSS for layout — browser handles CJK line-breaking,
 * font rendering, and rounded-rect natively.
 */

import type { CardInput } from "./types.js";
import {
  type ZoneLayout, LEFT_ZONE, RIGHT_ZONE,
  PAGE_WIDTH_IN, PAGE_HEIGHT_IN, DPI,
  BLUE_FILL, ORANGE_FILL, TITLE_COLOR, BODY_COLOR, HIGHLIGHT_COLOR, PILL_TEXT_COLOR,
  FONT_CJK, FONT_LATIN, PILL_FONT_SIZE_IN, TITLE_FONT_SIZE_IN, TITLE_POS,
  parseIn, inToPx, pillLayout,
} from "./layout.js";

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function highlightDesc(text: string): string {
  return escHtml(text).replace(
    /「([^」]+)」/g,
    '<b style="color:' + HIGHLIGHT_COLOR + ';font-family:Arial,sans-serif">「$1」</b>',
  );
}

function renderZone(card: CardInput, zone: ZoneLayout, imageUrls: string[]): string {
  const hasOrange = !!card.techLabel;
  const ipX = parseIn(zone.imagePos[0]), ipY = parseIn(zone.imagePos[1]);
  const isW = parseIn(zone.imageSize[0]), isH = parseIn(zone.imageSize[1]);
  const pills = pillLayout(zone, hasOrange);
  const tbPos = hasOrange ? zone.textboxPos : zone.textboxPosNoOrange;
  const tbSize = hasOrange ? zone.textboxSize : zone.textboxSizeNoOrange;
  const tpX = parseIn(tbPos[0]), tpY = parseIn(tbPos[1]);
  const tsW = parseIn(tbSize[0]), tsH = parseIn(tbSize[1]);

  const pillFontPx = inToPx(PILL_FONT_SIZE_IN);

  // Image grid
  const n = imageUrls.length;
  const cols = n <= 1 ? 1 : n <= 2 ? 2 : n <= 4 ? 2 : 3;
  const rows = Math.ceil(n / cols);
  const gap = 0.06;
  const cellW = (isW - gap * (cols - 1)) / cols;
  const cellH = (isH - gap * (rows - 1)) / rows;

  let imagesHtml = "";
  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = ipX + col * (cellW + gap);
    const cy = ipY + row * (cellH + gap);
    imagesHtml += `<img src="${imageUrls[i]}" style="
      position:absolute; left:${inToPx(cx)}px; top:${inToPx(cy)}px;
      width:${inToPx(cellW)}px; height:${inToPx(cellH)}px;
      object-fit:contain; border-radius:4px;
    " />`;
  }

  // Blue pill
  const bp = pills.blue;
  const bluePill = `<div style="
    position:absolute; left:${inToPx(bp.x)}px; top:${inToPx(bp.y)}px;
    width:${inToPx(bp.w)}px; height:${inToPx(bp.h)}px;
    background:${BLUE_FILL}; border-radius:${inToPx(0.06)}px;
    display:flex; align-items:center; justify-content:center;
    color:${PILL_TEXT_COLOR}; font-size:${pillFontPx}px; font-weight:bold;
    font-family:${FONT_CJK}; text-align:center; padding:0 8px;
    box-sizing:border-box; overflow:hidden;
  ">${escHtml(card.title)}</div>`;

  // Orange pill
  let orangePill = "";
  if (hasOrange && pills.orange) {
    const op = pills.orange;
    orangePill = `<div style="
      position:absolute; left:${inToPx(op.x)}px; top:${inToPx(op.y)}px;
      width:${inToPx(op.w)}px; height:${inToPx(op.h)}px;
      background:${ORANGE_FILL};
      display:flex; align-items:center; justify-content:center;
      color:${PILL_TEXT_COLOR}; font-size:${pillFontPx}px; font-weight:bold;
      font-family:${FONT_CJK}; text-align:center; padding:0 8px;
      box-sizing:border-box; overflow:hidden;
    ">${escHtml(card.techLabel!)}</div>`;
  }

  // Text box
  const bodyFontPx = inToPx(PILL_FONT_SIZE_IN);
  let textLines = `<div style="margin-bottom:2px"> - 規格：${escHtml(card.spec)}</div>`;
  if (card.topic) {
    textLines += `<div style="margin-bottom:2px"> - 題目：${escHtml(card.topic)}</div>`;
  }
  textLines += `<div> - 說明：${highlightDesc(card.description)}</div>`;

  const textBox = `<div style="
    position:absolute; left:${inToPx(tpX)}px; top:${inToPx(tpY)}px;
    width:${inToPx(tsW)}px; height:${inToPx(tsH)}px;
    font-family:${FONT_CJK}; font-size:${bodyFontPx}px; color:${BODY_COLOR};
    line-height:1.15; overflow:hidden;
  ">${textLines}</div>`;

  return imagesHtml + bluePill + orangePill + textBox;
}

/**
 * Render a card preview as a scaled HTML element inside the container.
 * The preview shows one slide page at the specified scale.
 */
export function renderCardPreview(
  container: HTMLElement,
  card: CardInput,
  zone: "left" | "right",
  imageUrls: string[],
  scale = 0.5,
): void {
  const zoneLayout = zone === "left" ? LEFT_ZONE : RIGHT_ZONE;
  const pw = Math.round(PAGE_WIDTH_IN * DPI);
  const ph = Math.round(PAGE_HEIGHT_IN * DPI);
  const titleFontPx = inToPx(TITLE_FONT_SIZE_IN);

  const title = `<div style="
    position:absolute; left:${inToPx(TITLE_POS.x)}px; top:${inToPx(TITLE_POS.y)}px;
    width:${inToPx(TITLE_POS.w)}px; height:${inToPx(TITLE_POS.h)}px;
    font-family:${FONT_LATIN}; font-size:${titleFontPx}px; font-weight:bold;
    color:${TITLE_COLOR}; display:flex; align-items:center;
  ">貼文切角建議</div>`;

  const cardHtml = renderZone(card, zoneLayout, imageUrls);

  container.innerHTML = `<div class="card-preview-wrapper" style="
    width:${pw * scale}px; height:${ph * scale}px;
    overflow:hidden; border:1px solid #e5e7eb; border-radius:8px;
    background:#f9fafb; position:relative;
  "><div style="
    width:${pw}px; height:${ph}px; background:white;
    position:relative; transform-origin:top left;
    transform:scale(${scale}); box-shadow:0 2px 8px rgba(0,0,0,0.08);
  ">${title}${cardHtml}</div></div>`;
}

/**
 * Create object URLs from File objects for preview images.
 * Caller must revoke these URLs when done.
 */
export function createImageUrls(files: File[]): string[] {
  return files.map(f => URL.createObjectURL(f));
}

export function revokeImageUrls(urls: string[]): void {
  urls.forEach(u => URL.revokeObjectURL(u));
}
